import { ChangeEvent, useState } from 'react';
import 'react-phone-input-2/lib/style.css';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import styled from 'styled-components';
import { HidePassword, ShowPassword } from '../../assets/Icons';
import { Button } from '../../components/atoms';
import { InputField } from '../../components/atoms/InputField/InputField';
import { useMutationHook } from '../../network/mutations/useMutationHook';
import { adminLogin } from '../../network/services/admin/auth';
import { userIsLoggedIn, userRoleAtom } from '../../store/atoms/userAtom';
import { validators } from '../../utils/validator';

const INITIALSTATE = {
  email: '',
  password: ''
};

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 480px;
  padding: 40px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }

  h1 {
    color: #1a1a1a;
    font-size: 32px;
    font-weight: 700;
    margin: 0 0 12px;
    text-align: center;
  }

  p {
    color: #666;
    text-align: center;
    margin: 0 0 32px;
    font-size: 15px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 24px;

  label {
    display: block;
    margin-bottom: 8px;
    color: #4a4a4a;
    font-weight: 500;
    font-size: 14px;
  }

  .error {
    color: #ff3d00;
    font-size: 12px;
    margin-top: 4px;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transition: all 0.3s ease;
  margin-top: 32px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PasswordWrapper = styled.div`
  position: relative;

  button {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    transition: color 0.2s ease;

    &:hover {
      color: #2563eb;
    }
  }
`;

const inputStyles = {
  height: '48px',
  fontSize: '15px',
  borderRadius: '12px',
  border: '1.5px solid #e2e8f0',
  padding: '0 16px',
  transition: 'all 0.2s ease',
  width: '100%',
  '&:focus': {
    borderColor: '#2563eb',
    boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)'
  }
};

const Login = () => {
  const [formData, setFormData] = useState(INITIALSTATE);
  const [formError, setFormError] = useState(INITIALSTATE);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<'text' | 'password'>('password');

  const onUpdateFormData = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;
    const error = validators[name](value);
    setFormData(formData => ({ ...formData, [name]: value }));
    setFormError({
      ...formError,
      [name]: error
    });
  };

  const onTogglePassword = () => {
    setShowPassword(prev => (prev === 'text' ? 'password' : 'text'));
  };

  const setUserStatus = useSetRecoilState(userIsLoggedIn);
  const setUserRole = useSetRecoilState(userRoleAtom);
  const { mutateAsync, isLoading } = useMutationHook(adminLogin, 'LOGIN');

  const _login = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    try {
      const { data } = await mutateAsync(formData);

      if (data.status === 'success') {
        sessionStorage.setItem('access_token', data.access_token);
        sessionStorage.setItem('refresh_token', data.refresh_token);
        sessionStorage.setItem('is_login', 'true');
        setUserStatus(true);
        setUserRole('admin');
        navigate('/admin/dashboard');
      }
    } catch (error: any) {}
  };

  const isDisabled = !formData.email || !formData.password || !!formError.email;

  return (
    <LoginContainer>
      <LoginCard>
        <h1>Welcome Back</h1>
        <p>Sign in to access your admin dashboard</p>

        <form onSubmit={e => e.preventDefault()}>
          <FormGroup>
            <label htmlFor="email">Email Address</label>
            <InputField
              type="email"
              name="email"
              onChange={onUpdateFormData}
              placeholder="you@company.com"
              extraStyles={inputStyles}
              required
              error={formError.email}
              autoComplete="username"
            />
            {formError.email && <div className="error">{formError.email}</div>}
          </FormGroup>

          <FormGroup>
            <label htmlFor="password">Password</label>
            <PasswordWrapper>
              <InputField
                type={showPassword}
                name="password"
                onChange={onUpdateFormData}
                placeholder="Enter your password"
                extraStyles={inputStyles}
                required
                autoComplete="current-password"
              />
              <button type="button" onClick={onTogglePassword}>
                {showPassword === 'text' ? <HidePassword /> : <ShowPassword />}
              </button>
            </PasswordWrapper>
          </FormGroup>

          <StyledButton isLoading={isLoading} type="submit" onClick={_login} disabled={isDisabled}>
            {isLoading ? 'Signing in...' : 'Sign in'}
          </StyledButton>
        </form>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
